/* ===== pageRouter.ts — الموجِّه الأعلى: صفحة واحدة، ثلاث «صفحات» =====
   كان الأصل ثلاثة ملفات HTML (التطبيق · الدخول · التوقيع) والمسار في الـhash. الآن
   index.html واحد وجذر React واحد، ويختار الموجِّه الصفحة من location.pathname
   (routes.ts → pageOf): ‎/login‎ · ‎/sign?t=…‎ · وكل ما سواهما التطبيق (‎/‎ = الرئيسية،
   ‎/plan‎ …، والمجهول يرسم الرئيسية بلا تبويب مضاء كالأصل).

   تحميل كل صفحة كسول (import()): ما تحمّله الصفحة هو شجرة استيراد وحدتها وحدها،
   كما كانت وسوم <script> في ملفها — فلا تُقيَّم وحدات المخزن في صفحة الدخول ولا
   شاشات التطبيق في بوابة التوقيع.

   قبل أول رسم: رابط قديم بالـhash في الجذر «/#plan» يُستبدل بـ«/plan»
   (history.replaceState، بلا إعادة تحميل). مسار معروف بالضبط فقط — شظية المصادقة
   ‎#access_token=…‎ و ‎?code=…‎ لا تُمسّ أبداً: يقرؤها supabase-js ثم يمسحها بنفسه.

   الانتقال بين الصفحات (openPage في pageNav.ts وزرّا الرجوع/التقدّم):
   - ما دام التطبيق لم يُقلع في هذا المستند ⇒ تبديل داخل المستند: pushState، ثم
     يُفرَّغ الجذر، ويُطبَّق غلاف الصفحة الجديدة (أنماطها وعنوانها وسماتها — pageShell.ts)
     ثم تُركَّب. هذا طريق الدخول الناجح: /login ⇒ /dashboard بلا إعادة تحميل.
   - بعد إقلاع التطبيق ⇒ تنقّل كامل للمستند (location / reload) كما كان الأصل دائماً:
     التطبيق يحمل حالة في الذاكرة (المخزن · البث اللحظي · المؤقّتات · مستمعات عامة)
     لا يُسقطها إلا مستند جديد — وهذا هو المطلوب عند الخروج أو انتهاء الجلسة. */

import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import { applyPageShell, type PageShell } from './pageShell';
import { legacyHashTarget, pageOf, type PageKind } from './routes';
import { setPageNavigator } from './pageNav';

export interface PageModule {
  shell: PageShell;
  start(root: Root): void;
}

export type PageLoaders = Record<PageKind, () => Promise<PageModule>>;

export interface PageRouterOptions {
  host: HTMLElement;
  loaders: PageLoaders;
  /** تنقّل كامل للمستند (يُستبدل في الاختبارات). */
  hardNavigate?: (url: string) => void;
  /** إعادة تحميل المستند على العنوان الحالي (يُستبدل في الاختبارات). */
  reload?: () => void;
}

export interface PageRouter {
  start(): Promise<void>;
  open(url: string): Promise<void>;
  current(): PageKind | null;
  stop(): void;
}

/** «/#plan» ⇒ «/plan» قبل أول رسم. يُرجع true إن غيّر العنوان. */
export function normalizeInitialUrl(): boolean {
  const target = legacyHashTarget(window.location);
  if (target === null) return false;
  window.history.replaceState(window.history.state, '', target);
  return true;
}

export function createPageRouter(opts: PageRouterOptions): PageRouter {
  const { host, loaders } = opts;
  const hardNavigate = opts.hardNavigate || ((url: string) => window.location.assign(url));
  const reload = opts.reload || (() => window.location.reload());
  const root = createRoot(host);
  let current: PageKind | null = null;
  let appStarted = false;
  let seq = 0;

  async function show(kind: PageKind): Promise<void> {
    const my = ++seq;
    const mod = await loaders[kind]();
    if (my !== seq) return; // انتقال أحدث سبقه
    // تفريغ الصفحة السابقة متزامناً قبل تبديل الأنماط: لا إطار تُرسم فيه صفحة
    // بأنماط غيرها.
    if (current !== null) flushSync(() => root.render(null));
    applyPageShell(mod.shell, host);
    current = kind;
    if (kind === 'app') appStarted = true;
    mod.start(root);
  }

  function onPop(): void {
    const kind = pageOf(window.location.pathname);
    if (kind === current) return; // تنقّل داخل الصفحة نفسها — للتطبيق popstate الخاص به
    if (appStarted) reload();
    else void show(kind);
  }

  async function open(url: string): Promise<void> {
    const target = new URL(url, window.location.href);
    const kind = pageOf(target.pathname);
    if (appStarted || target.origin !== window.location.origin) {
      hardNavigate(target.href);
      return;
    }
    window.history.pushState(null, '', target.pathname + target.search + target.hash);
    await show(kind);
  }

  return {
    async start() {
      normalizeInitialUrl();
      window.addEventListener('popstate', onPop);
      setPageNavigator((url) => void open(url));
      await show(pageOf(window.location.pathname));
    },
    open,
    current: () => current,
    stop() {
      window.removeEventListener('popstate', onPop);
      setPageNavigator(null);
      root.unmount();
    },
  };
}
